import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LockKey, Eye, EyeSlash } from '@phosphor-icons/react';

const MIN_LENGTH = 8;
const EMPTY = { current: '', next: '', confirm: '' };

export const policyError = (next, current) => {
  if (next.length < MIN_LENGTH) return `New password must be at least ${MIN_LENGTH} characters`;
  if (!/[A-Z]/.test(next) || !/[a-z]/.test(next) || !/\d/.test(next)) {
    return 'New password must contain at least one uppercase letter, one lowercase letter, and one digit';
  }
  if (next === current) return 'New password must be different from current password';
  return null;
};

const Field = ({ id, label, value, onChange, autoComplete, show, describedBy }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
    <Input
      id={id}
      name={id}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      required
      aria-describedby={describedBy}
      data-testid={`${id}-input`}
    />
  </div>
);

export const ChangePasswordCard = () => {
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    const clientError =
      (!form.current && 'Current password is required') ||
      policyError(form.next, form.current) ||
      (form.next !== form.confirm && 'New password and confirmation do not match') ||
      null;
    if (clientError) {
      setError(clientError);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/auth/change-password', {
        current_password: form.current,
        new_password: form.next,
      });
      const revoked = r.data?.other_sessions_revoked ?? 0;
      setResult({ revoked });
      toast.success('Password changed');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Password change failed');
    } finally {
      setForm(EMPTY);
      setShow(false);
      setBusy(false);
    }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <LockKey size={18} className="text-slate-700" weight="duotone" />
          Change password
        </CardTitle>
        <CardDescription>
          Requires your current password. Every other signed-in session for this account is revoked;
          this session stays active. Minimum {MIN_LENGTH} characters with an uppercase letter, a lowercase
          letter and a digit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4 max-w-md" noValidate data-testid="change-password-form">
          <Field id="current-password" label="Current password" value={form.current} onChange={set('current')} autoComplete="current-password" show={show} describedBy="change-password-status" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="new-password" label="New password" value={form.next} onChange={set('next')} autoComplete="new-password" show={show} describedBy="change-password-status" />
            <Field id="confirm-password" label="Confirm new password" value={form.confirm} onChange={set('confirm')} autoComplete="new-password" show={show} describedBy="change-password-status" />
          </div>
          <div id="change-password-status" aria-live="polite" className="min-h-[1.25rem]">
            {error && (
              <p className="text-sm text-red-700" role="alert" data-testid="change-password-error">{error}</p>
            )}
            {result && (
              <p className="text-sm text-emerald-700" data-testid="change-password-success">
                Password changed. Other sessions revoked: <span data-testid="change-password-revoked-count">{result.revoked}</span>.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={busy} data-testid="change-password-submit">
              {busy ? 'Changing…' : 'Change password'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShow((s) => !s)}
              aria-pressed={show}
              data-testid="change-password-toggle-visibility"
            >
              {show ? <EyeSlash size={16} className="mr-1.5" /> : <Eye size={16} className="mr-1.5" />}
              {show ? 'Hide' : 'Show'} passwords
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordCard;
